// ─── Public Status Page — the day-grid tooltip ───────────────────────
//
// ONE tooltip node for the ENTIRE page, portalled to <body> and moved around by
// HistoryGrid. That is the whole design: the overview page draws ~28 component
// strips × up to 90 day cells ≈ 2,500 nodes, and every naive way of labelling
// them is unacceptable —
//
//   * a native `title=` on each cell: 2,500 attributes, ~700 ms of hover delay,
//     unstyleable, and completely invisible on touch;
//   * one React tooltip per cell: 2,500 mounted components and 2,500 listeners;
//   * one tooltip per ROW: still 28 nodes and 28 pieces of positioning state.
//
// So the cells carry nothing but `data-c`/`data-d`, the grid delegates pointer
// events, and this component renders exactly once with a viewport rect handed to
// it. It is portalled because a status card is inside `overflow: hidden` cards
// and a sticky, `backdrop-filter`ed header — either of which would clip or
// re-anchor a positioned child.
//
// It is `aria-hidden` on purpose: the information it shows is already in the
// strip's `aria-label` and in the visually-hidden day list rendered by
// HistoryGridRow, so exposing it again would make a screen reader read every day
// twice. Keyboard users get the same text through the grid's live region.

import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';

/** A cell's rect in VIEWPORT coordinates (straight from getBoundingClientRect). */
export interface HistoryTooltipAnchor {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface HistoryTooltipProps {
  anchor: HistoryTooltipAnchor;
  /** The bar's own name — a component name, or the page-wide label. */
  title: string;
  /** The day, ALREADY formatted in the reader's locale by lib/format. */
  day: string;
  /** Danish status label for that day (lib/levels). */
  statusLabel: string;
  /** A `var(--…)` token reference from lib/levels — never a literal colour. */
  statusColor: string;
  /** Titles of the incidents that touched this component on this day. */
  incidents: readonly string[];
}

/** Minimum gap to the viewport edge, and between the cell and the bubble. */
const EDGE = 8;

interface Placement {
  left: number;
  top: number;
  below: boolean;
}

export function HistoryTooltip({ anchor, title, day, statusLabel, statusColor, incidents }: HistoryTooltipProps) {
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const [placement, setPlacement] = useState<Placement | null>(null);

  // The bubble is measured AFTER it renders (its width depends on the incident
  // titles inside it), so positioning is a layout effect: it runs before the
  // browser paints, which means the first frame is already in the right place —
  // no flash at the top-left corner. `visibility: hidden` covers the very first
  // pass, before any measurement exists.
  const incidentKey = incidents.join('\u0000');
  useLayoutEffect(() => {
    const node = nodeRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const viewportW = window.innerWidth || rect.width;
    const viewportH = window.innerHeight || rect.height;

    // Centre on the cell, then clamp inside the viewport — a day near either end
    // of a 90-cell bar would otherwise hang off the screen.
    const centred = anchor.left + anchor.width / 2 - rect.width / 2;
    const maxLeft = Math.max(EDGE, viewportW - rect.width - EDGE);
    const left = Math.min(Math.max(EDGE, centred), maxLeft);

    // Above the cell by default; flip below when the bar sits near the top of
    // the window (which it does on a phone, under the sticky header).
    let top = anchor.top - rect.height - EDGE;
    let below = false;
    if (top < EDGE) {
      top = anchor.top + anchor.height + EDGE;
      below = true;
    }
    if (top + rect.height > viewportH - EDGE) {
      top = Math.max(EDGE, viewportH - rect.height - EDGE);
    }

    setPlacement((prev) =>
      prev && prev.left === left && prev.top === top && prev.below === below ? prev : { left, top, below },
    );
  }, [anchor.top, anchor.left, anchor.width, anchor.height, title, day, statusLabel, incidentKey]);

  const style: CSSProperties = {
    left: placement ? placement.left : 0,
    top: placement ? placement.top : 0,
    visibility: placement ? 'visible' : 'hidden',
  };

  return createPortal(
    <div
      ref={nodeRef}
      className={`sg-tip${placement?.below ? ' sg-tip--below' : ''}`}
      style={style}
      aria-hidden="true"
    >
      {title ? <p className="sg-tip-title">{title}</p> : null}
      <p className="sg-tip-day">{day}</p>
      <p className="sg-tip-status">
        <span className="sg-tip-dot" style={{ ['--sg-c' as string]: statusColor } as CSSProperties} />
        {statusLabel}
      </p>
      {incidents.length > 0 && (
        <ul className="sg-tip-incidents">
          {incidents.map((incident, index) => (
            <li key={`${index}-${incident}`}>{incident}</li>
          ))}
        </ul>
      )}
    </div>,
    document.body,
  );
}
